import { zodResolver } from '@hookform/resolvers/zod';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, ExternalLink, MoreHorizontal, Plus, Settings, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import Header from '../components/Header';
import { AssetCategoryListItem, fetchAllAssetCategories } from '../lib/assetCategories';
import { Badge } from '../components/ui/badge';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Button } from '../components/ui/button';
import { createBadgeColumn, createDateColumn, createLinkColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

type AssetCategory = AssetCategoryListItem;

interface CategoryStats {
  total: number;
  active: number;
  totalProducts: number;
  empty: number;
}

type CategoryFilter = 'all' | 'active' | 'empty';

const categorySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(1000).optional(),
  is_active: z.boolean().optional(),
});

type CategoryForm = z.infer<typeof categorySchema>;

export default function Categories() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [allCategories, setAllCategories] = useState<AssetCategory[]>([]);
  const [stats, setStats] = useState<CategoryStats | null>(null);
  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AssetCategory | null>(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<AssetCategory | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const form = useForm<CategoryForm>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: '',
      description: '',
      is_active: true,
    },
  });

  useEffect(() => {
    checkAuth();
    loadCategories();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply client-side filtering when filter or allCategories change
  useEffect(() => {
    if (filter === 'all') {
      setCategories(allCategories);
    } else if (filter === 'active') {
      setCategories(allCategories.filter(c => c.is_active));
    } else if (filter === 'empty') {
      setCategories(allCategories.filter(c => {
        const cat = c as AssetCategory & { templates?: { id: string }[] };
        return !cat.templates || cat.templates.length === 0;
      }));
    }
  }, [filter, allCategories]);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (!token || !savedUser) {
      navigate('/login');
      return;
    }

    try {
      setUser(JSON.parse(savedUser));
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await fetchAllAssetCategories(true);
      setAllCategories(categoriesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/asset-categories/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading category stats:', err);
    }
  };

  const handleFilterClick = (newFilter: CategoryFilter) => {
    setFilter(newFilter);
  };

  const startCreate = () => {
    setEditing(null);
    form.reset({ name: '', description: '', is_active: true });
    setDialogOpen(true);
  };

  const startEdit = (category: AssetCategory) => {
    setEditing(category);
    form.reset({
      name: category.name,
      description: category.description || '',
      is_active: category.is_active,
    });
    setDialogOpen(true);
  };

  const onSubmit = async (values: CategoryForm) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editing ? `/api/asset-categories/${editing.id}` : '/api/asset-categories';
      const method = editing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        await loadCategories();
        setDialogOpen(false);
        form.reset();
      } else {
        console.error('Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (category: AssetCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/asset-categories/${categoryToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
        await loadCategories();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage('Failed to delete category');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting the category');
      setErrorDialogOpen(true);
    }
  };

  const canEdit = user?.roles?.some(role => ['admin', 'supervisor'].includes(role.key)) ?? false;
  const canDelete = user?.roles?.some(role => role.key === 'admin') ?? false;

  const columns: ColumnDef<AssetCategory>[] = [
    createLinkColumn<AssetCategory>('name', 'Name', (category) =>
      navigate(`/categories/${category.id}/features`)
    ),
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => {
        const description = row.getValue('description') as string | null;
        return description ? (
          <span className="text-sm">{description}</span>
        ) : (
          <span className="text-gray-400 italic">No description</span>
        );
      },
    },
    {
      accessorKey: 'features',
      header: 'Features',
      cell: ({ row }) => {
        const category = row.original;
        const featuresCount = category.features?.length || 0;
        return (
          <span className="text-sm">
            {featuresCount} feature{featuresCount !== 1 ? 's' : ''}
          </span>
        );
      },
    },
    {
      accessorKey: 'templates',
      header: 'Products',
      cell: ({ row }) => {
        const category = row.original as AssetCategory & { templates?: { id: string }[] };
        const productsCount = category.templates?.length || 0;
        return (
          <Link
            to={`/assets?category_id=${category.id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            {productsCount} product{productsCount !== 1 ? 's' : ''}
            <ExternalLink className="h-3 w-3" />
          </Link>
        );
      },
    },
    {
      accessorKey: 'roomTypes',
      header: 'Room Types',
      cell: ({ row }) => {
        const roomTypes = row.original.roomTypes || [];
        return (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {roomTypes.slice(0, 3).map(rt => (
              <Badge key={rt.id} variant="outline" className="text-xs">
                {rt.name}
              </Badge>
            ))}
            {roomTypes.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{roomTypes.length - 3} more
              </Badge>
            )}
            {roomTypes.length === 0 && (
              <span className="text-muted-foreground text-sm italic">None</span>
            )}
          </div>
        );
      },
    },
    createBadgeColumn<AssetCategory>('is_active', 'Status', {
      getBadgeVariant: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'default' : 'secondary';
      },
      formatValue: (value) => {
        const isActive = typeof value === 'boolean' ? value : value === 'true';
        return isActive ? 'Active' : 'Inactive';
      },
    }),
    createDateColumn<AssetCategory>('created_at', 'Created At', 'datetime'),
    {
      id: 'actions',
      header: 'Actions',
      enableHiding: false,
      cell: ({ row }) => {
        const category = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/categories/${category.id}/features`)}>
                <Settings className="h-4 w-4 mr-2" />
                Manage Features
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={() => startEdit(category)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Category
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => handleDeleteClick(category)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Category
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="categories" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="categories" />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold text-primary leading-tight mb-2">
            Asset Categories
          </h2>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => handleFilterClick('all')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              filter === 'all' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Categories</div>
          </div>
          <div
            onClick={() => handleFilterClick('active')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              filter === 'active' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{stats?.active || 0}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </div>
          <div className="bg-card p-6 rounded-lg shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{stats?.totalProducts || 0}</div>
            <div className="text-sm text-muted-foreground">Total Products</div>
          </div>
          <div
            onClick={() => handleFilterClick('empty')}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              filter === 'empty' ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-600">{stats?.empty || 0}</div>
            <div className="text-sm text-muted-foreground">Empty Categories</div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={categories}
          loading={loading}
          searchKey="name"
          searchPlaceholder="Search categories..."
          toolbarContentRight={canEdit ? (
            <Button onClick={startCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          ) : undefined}
          emptyMessage="No categories found."
        />

        {/* Create/Edit Category Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Category' : 'Create Category'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Update the category details.' : 'Add a new furniture category.'}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Sofa, Coffee Table" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Describe this category of furniture..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : editing ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Category"
          description={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={deleteCategory}
          variant="destructive"
        />

        {/* Error Message Dialog */}
        <MessageDialog
          open={errorDialogOpen}
          onOpenChange={setErrorDialogOpen}
          title="Error"
          description={errorMessage}
          variant="destructive"
        />
      </main>
    </div>
  );
}
